import { useEffect, useRef, useState } from 'react';
import { findFacets, type PandoraFacet, type PandoraQuery } from '../../api/pandora';

interface Props {
  site: string;
  /** selectable facet keys (computed by the overlay from the site's itemKeys) */
  facetKeys: string[];
  facetKey: string;
  onFacetKey(key: string): void;
  query: PandoraQuery;
  /** values of `facetKey ==` conditions currently in the query */
  activeValues: Set<string>;
  onToggle(name: string): void;
}

/** Facet counts for the current query; clicking a value toggles a `==` condition. */
export default function FacetSidebar({
  site,
  facetKeys,
  facetKey,
  onFacetKey,
  query,
  activeValues,
  onToggle,
}: Props) {
  const [facets, setFacets] = useState<PandoraFacet[]>([]);
  const [loading, setLoading] = useState(false);
  const generation = useRef(0);

  useEffect(() => {
    const gen = ++generation.current;
    setLoading(true);
    const t = setTimeout(() => {
      findFacets(site, query, facetKey).then(
        (list) => {
          if (gen !== generation.current) return;
          setFacets(list);
          setLoading(false);
        },
        () => {
          if (gen !== generation.current) return;
          setFacets([]);
          setLoading(false);
        },
      );
    }, 350);
    return () => clearTimeout(t);
  }, [site, query, facetKey]);

  return (
    <div className="adv-facets">
      <select value={facetKey} onChange={(e) => onFacetKey(e.target.value)}>
        {facetKeys.map((k) => (
          <option key={k} value={k}>
            {k}
          </option>
        ))}
      </select>
      <div className="adv-facet-list">
        {loading && <div className="search-hint">loading…</div>}
        {!loading && facets.length === 0 && <div className="search-hint">no values</div>}
        {!loading &&
          facets.map((f) => (
            <button
              key={f.name || '(none)'}
              className={`adv-facet ${activeValues.has(f.name) ? 'active' : ''}`}
              onClick={() => onToggle(f.name)}
            >
              <span className="adv-facet-name">{f.name || '(none)'}</span>
              <span className="adv-facet-count">{f.items}</span>
            </button>
          ))}
      </div>
    </div>
  );
}
